/**
 * Isometric Building Component
 * Simple wireframe building in isometric view for hero background
 */

export function IsometricBuilding() {
    return (
        <g id="building-structure

" className="building-wireframe">
            {/* Floor 1 (Base) */}
            <polygon
                points="200,400 400,300 600,400 400,500"
                fill="none"
                stroke="var(--blueprint-line)"
                strokeWidth="2"
                opacity="0.6"
            />

            {/* Floor 2 */}
            <polygon
                points="200,350 400,250 600,350 400,450"
                fill="none"
                stroke="var(--blueprint-line)"
                strokeWidth="2"
                opacity="0.7"
            />

            {/* Floor 3 */}
            <polygon
                points="200,300 400,200 600,300 400,400"
                fill="none"
                stroke="var(--blueprint-line)"
                strokeWidth="2"
                opacity="0.8"
            />

            {/* Roof */}
            <polygon
                points="200,250 400,150 600,250 400,350"
                fill="none"
                stroke="var(--blueprint-line-light)"
                strokeWidth="2"
                opacity="0.9"
            />

            {/* Vertical edges */}
            <line x1="200" y1="250" x2="200" y2="400" stroke="var(--blueprint-line)" strokeWidth="2" opacity="0.6" />
            <line x1="400" y1="150" x2="400" y2="500" stroke="var(--blueprint-line)" strokeWidth="2" opacity="0.7" />
            <line x1="600" y1="250" x2="600" y2="400" stroke="var(--blueprint-line)" strokeWidth="2" opacity="0.6" />

            {/* Internal grid lines for detail */}
            <line x1="300" y1="275" x2="300" y2="450" stroke="var(--blueprint-line)" strokeWidth="1" opacity="0.3" />
            <line x1="500" y1="275" x2="500" y2="450" stroke="var(--blueprint-line)" strokeWidth="1" opacity="0.3" />
        </g>
    );
}

/**
 * Detection Layer - Sensor nodes that light up
 */
interface DetectionLayerProps {
    animate?: boolean;
}

export function DetectionLayer({ animate = false }: DetectionLayerProps) {
    const sensors = [
        { x: 250, y: 380, floor: 1 },
        { x: 350, y: 320, floor: 1 },
        { x: 450, y: 380, floor: 1 },
        { x: 550, y: 320, floor: 1 },
        { x: 250, y: 330, floor: 2 },
        { x: 350, y: 270, floor: 2 },
        { x: 450, y: 330, floor: 2 },
        { x: 550, y: 270, floor: 2 },
        { x: 300, y: 280, floor: 3 },
        { x: 400, y: 220, floor: 3 },
        { x: 500, y: 280, floor: 3 },
    ];

    return (
        <g id="detection-layer" className={`detection-sensors ${animate ? 'active' : ''}`}>
            {sensors.map((sensor, i) => (
                <g key={i}>
                    {/* Sensor node */}
                    <circle
                        cx={sensor.x}
                        cy={sensor.y}
                        r="4"
                        fill="var(--fire-orange)"
                        opacity={animate ? "0.9" : "0"}
                        style={{
                            filter: 'drop-shadow(0 0 4px var(--fire-orange))',
                            animation: animate ? 'detectionPulse 2s ease-in-out infinite' : 'none',
                            animationDelay: `${i * 100}ms`
                        }}
                    />
                    {/* Glow ring */}
                    {animate && (
                        <circle
                            cx={sensor.x}
                            cy={sensor.y}
                            r="8"
                            fill="none"
                            stroke="var(--fire-orange)"
                            strokeWidth="1"
                            opacity="0.4"
                        />
                    )}
                </g>
            ))}
        </g>
    );
}

/**
 * Suppression Layer - Sprinkler system
 */
interface SuppressionLayerProps {
    animate?: boolean;
}

export function SuppressionLayer({ animate = false }: SuppressionLayerProps) {
    const sprinklers = [
        { x: 300, y: 360 },
        { x: 400, y: 300 },
        { x: 500, y: 360 },
        { x: 300, y: 310 },
        { x: 400, y: 250 },
        { x: 500, y: 310 },
    ];

    return (
        <g id="suppression-layer" className={`suppression-system ${animate ? 'active' : ''}`}>
            {sprinklers.map((sprinkler, i) => (
                <g key={i}>
                    {/* Sprinkler head */}
                    <rect
                        x={sprinkler.x - 3}
                        y={sprinkler.y - 3}
                        width="6"
                        height="6"
                        fill="var(--tech-cyan)"
                        opacity={animate ? "0.8" : "0"}
                        transform={`rotate(45 ${sprinkler.x} ${sprinkler.y})`}
                    />
                    {/* Coverage zone */}
                    {animate && (
                        <circle
                            cx={sprinkler.x}
                            cy={sprinkler.y}
                            r="25"
                            fill="rgba(0, 212, 255, 0.1)"
                            stroke="var(--tech-cyan)"
                            strokeWidth="0.5"
                            opacity="0.5"
                        />
                    )}
                </g>
            ))}
        </g>
    );
}

/**
 * Network Layer - Communication lines
 */
interface NetworkLayerProps {
    animate?: boolean;
}

export function NetworkLayer({ animate = false }: NetworkLayerProps) {
    const connections = [
        { x1: 400, y1: 500, x2: 400, y2: 150 }, // Central spine
        { x1: 400, y1: 400, x2: 200, y2: 350 }, // Floor 1-2 left
        { x1: 400, y1: 400, x2: 600, y2: 350 }, // Floor 1-2 right
        { x1: 400, y1: 300, x2: 200, y2: 300 }, // Floor 2-3 left
        { x1: 400, y1: 300, x2: 600, y2: 300 }, // Floor 2-3 right
    ];

    return (
        <g id="network-layer" className={`network-lines ${animate ? 'active' : ''}`}>
            {connections.map((conn, i) => (
                <line
                    key={i}
                    x1={conn.x1}
                    y1={conn.y1}
                    x2={conn.x2}
                    y2={conn.y2}
                    stroke="var(--tech-green)"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    opacity={animate ? "0.7" : "0"}
                    style={{
                        animation: animate ? 'data-flow 0.5s linear infinite' : 'none'
                    }}
                />
            ))}
        </g>
    );
}
